import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useClerk, useUser } from '@clerk/clerk-react'
import { useWorkspaceStore } from '../stores/workspaceStore'
import { useWorkspaceRole } from '../utils/useWorkspaceRole'
import { canAccessFeature, canCreateWorkspace, roleLabel } from '../utils/permissions'
import CreateWorkspaceModal from '../components/CreateWorkspaceModal'
import { useTheme } from '../theme/ThemeProvider'
import {
  NeedsYouContext,
  ShellContext,
  useAskEntityState,
  useDrawerRegistration,
  useDrawerRegistry,
} from './ShellContext'
import {
  AppGrid,
  Avatar,
  Badge,
  BottomBar,
  Button,
  CardHeader,
  Icon,
  IconButton,
  Menu,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  NavItem,
  NavRail,
  Popover,
  ThemeToggle,
  ToastProvider,
  TrustPill,
  useDrawerContent,
} from '../ds'
import { ICON_PATHS } from './icons'
import AskDock from './AskDock'
import { surfaceFor } from './askSuggestions'
import { useAskThread } from './useAskThread'
import InboxDrawer from './InboxDrawer'
import { useBreakpoint } from './useBreakpoint'
import { useDockLayout } from './useDockLayout'
import { useNeedsYou } from './useNeedsYou'
import { useMonthUsage } from './useMonthUsage'
import MonthUsageSection from './MonthUsageSection'
import { initialOf } from './initialOf'

// Three Surfaces (v5): what she did · what she knows · what she may do.
// Value is a fourth destination in the design but has no backend yet — no cost
// side on /attribution, no per-customer aggregate, no prior-period comparison —
// so it is deliberately absent rather than shipped as a dead rail item.
const NAV = [
  { key: 'ledger', label: 'Ledger', path: '/ledger', icon: ICON_PATHS.ledger },
  { key: 'knowledge', label: 'Knowledge', path: '/knowledge', icon: ICON_PATHS.knowledge },
  { key: 'authority', label: 'Authority', path: '/authority', icon: ICON_PATHS.authority },
]

// The pill reports the rung; it no longer sets it. Writing `workspace.mode`
// from the topbar put the highest-consequence act in the product one click
// from a bell, with a tooltip as its only warning. The ladder on Authority is
// now the only place that writes it. `TrustPill` carries the copy ("… — open
// Authority to change it") because it is handed an `onClick`.

const CHEV = 'M6 9l6 6 6-6'
const PLUS = 'M12 5v14M5 12h14'

const SHADOW_PILL_LABEL = 'Shadow · nothing sends'

// The greeting follows the clock. It used to follow the theme, so a merchant on
// the dark theme at 9am was told "Good evening".
function greetingFor(name: string): string {
  const h = new Date().getHours()
  const part = h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'
  return `${part}, ${name}`
}

export default function AppShell() {
  const navigate = useNavigate()
  const location = useLocation()
  const { theme, mode, cycleMode } = useTheme()
  const { signOut } = useClerk()
  const { user } = useUser()
  const {
    currentAccount,
    currentWorkspace,
    currentUser,
    userMembership,
    workspaces,
    switchWorkspace,
  } = useWorkspaceStore()

  // The role the badge shows is the one every gate reads (`useWorkspaceRole`), never
  // `currentUser.role`, which can describe another workspace (PR #237 round 4), worded by
  // `roleLabel` (round 5). No role yet → no badge, as before the PR: hidden until known.
  const { role } = useWorkspaceRole()
  const shownRole = roleLabel(role)

  const [ctxPanel, setCtxPanel] = useState<ReactNode>(null)
  const [inboxOpen, setInboxOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [newWorkspaceOpen, setNewWorkspaceOpen] = useState(false)
  // Below 900px the rail becomes a BottomBar and its utilities move into the
  // topbar's profile panel (D-28). The same band the dock reads.
  const narrow = useBreakpoint() === 'narrow'

  const setContextPanel = useCallback((node: ReactNode) => setCtxPanel(node), [])

  // Which record the merchant has open, for the Ask dock. Scoped to the live
  // workspace inside the hook, so what the provider publishes is already
  // scoped — no consumer can read another workspace's record.
  const askEntity = useAskEntityState(currentWorkspace?.id)

  // How many drawers are open, anywhere in the app. Explicitly registered by
  // each drawer rather than derived from the ask-entity pin — the Ask hand-off
  // keeps the pin after the screen unmounts, so a pin routinely outlives every
  // drawer (see `useDrawerRegistry`).
  const drawers = useDrawerRegistry()
  const { openDrawerCount, registerDrawer } = drawers
  // The Inbox is shell-owned, so it registers here rather than through
  // `useRegisterDrawer` — `useContext` inside the provider reads past it. It stays registered
  // through its slide-out (`onExited`), so the dock's count follows what is on screen.
  const inboxShown = useDrawerContent(inboxOpen || null, inboxOpen)
  useDrawerRegistration(registerDrawer, !!inboxShown.value)

  // ⚠️ The context panel is part of the dock's arithmetic: it is a 312px grid
  // track, so whether it is open changes how much room the dock may take
  // before the canvas is squeezed out and the page scrolls sideways.
  const dock = useDockLayout(openDrawerCount, !!ctxPanel)
  const { setCollapsed: setDockCollapsed, setWidth: setDockWidth } = dock

  // ⚠️ `--dockw` on <html> as well as on `.mv-root` (below). Every design-system
  // Drawer, Scrim and Toast portals to <body> — outside `.mv-root` — and is inset
  // by this value so it stops at the dock's left edge. A drawer that cannot see
  // the property is a drawer that covers the dock. Layout effect, so the first
  // paint of an open drawer already has it.
  useLayoutEffect(() => {
    const el = document.documentElement
    el.style.setProperty('--dockw', `${dock.trackWidth}px`)
    return () => {
      el.style.removeProperty('--dockw')
    }
  }, [dock.trackWidth])
  // Bumped by every door into the dock; it focuses its composer on any change.
  const [dockFocus, setDockFocus] = useState(0)

  /**
   * The one door into the dock — ⌘K, the rail item and a
   * drawer's "Ask" button all come through here. Published on the shell value
   * so a screen can open it without reaching into the dock's state.
   */
  /**
   * ⚠️ `draft` is checked with `typeof`, not trusted from the type: `openAsk` is
   * handed straight to `onClick` in places, and a click passes its EVENT as the
   * first argument — which must open the dock, never fill the composer with
   * "[object Object]".
   */
  const [dockDraft, setDockDraft] = useState<{ text: string; nonce: number } | null>(null)
  const openAsk = useCallback(
    (draft?: unknown) => {
      setDockCollapsed(false)
      setDockFocus((n) => n + 1)
      if (typeof draft === 'string' && draft.trim()) {
        setDockDraft((d) => ({ text: draft, nonce: (d?.nonce ?? 0) + 1 }))
      }
    },
    [setDockCollapsed]
  )

  // The conversation. Held here rather than in `AskDock` because it must be
  // scoped to the live workspace at the READ and must survive the dock
  // collapsing — a collapsed dock unmounts its transcript.
  const thread = useAskThread(currentWorkspace?.id, currentAccount?.id)

  const shellValue = useMemo(
    () => ({ setContextPanel, ...askEntity, ...drawers, openAsk }),
    [setContextPanel, askEntity, drawers, openAsk]
  )

  const firstName = currentUser?.firstName || user?.firstName || 'there'
  const userInitial = (currentUser?.firstName?.[0] || user?.firstName?.[0] || 'U').toUpperCase()
  // The profile control's name. ⚠️ Never empty (PR #237 round 6): with the role badge hidden
  // until the role is known, a Clerk user with no first name had `title=""` and an accessible
  // name of just the initial. First name, then the email, then "Account".
  const profileName =
    currentUser?.firstName ||
    user?.firstName ||
    currentUser?.email ||
    user?.primaryEmailAddress?.emailAddress ||
    'Account'
  const profileLabel = `${profileName}${shownRole ? ` · ${shownRole}` : ''}`
  const greet = greetingFor(firstName)
  const sub = useMemo(() => {
    const date = new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })
    return currentWorkspace?.name ? `${date} · ${currentWorkspace.name}` : date
  }, [currentWorkspace?.name])

  // One definition of "waiting on you", shared by the bell badge, the Inbox
  // drawer and Ledger's waiting/Shadow banners (through `NeedsYouContext`).
  // Today used to compute a different number from its own fetches; it is
  // retired (MAR-309). Refetched on navigation (resolving elsewhere changes the
  // count) and on a slow interval.
  const isShadow = currentWorkspace?.mode === 'shadow'
  const needsYou = useNeedsYou(currentWorkspace?.id, !!isShadow, [location.pathname])

  const openInbox = useCallback(() => setInboxOpen(true), [])
  // `useNeedsYou` returns one object identity for as long as the queue says
  // the same thing (stable empty constants, a memoised result), so this keys on
  // the object itself. The shell re-renders on every streamed Ask token; without
  // that, each token would hand Ledger a new context value and re-render it.
  const needsYouValue = useMemo(() => ({ needsYou, openInbox }), [needsYou, openInbox])

  /**
   * What the dock says it can see while the Inbox is open (v6's
   * `pinKind: 'Reading your notifications'`). `InboxDrawer` publishes no
   * ask-entity — there is no single record open — so without this the dock sat
   * there saying "Reading the Ledger with you" with a drawer in front of it.
   *
   * ⚠️ Same rule as the bell badge directly below: an unread queue must never
   * look like an empty one, so a count that has not been read yet, or could not
   * be, says so rather than reporting "Nothing waiting".
   */
  // ⚠️ `degraded` before `incomplete`: both can be true at once, and checking
  // `incomplete` first would report a queue that FAILED to read as one still
  // being read.
  //
  // ⚠️ And NO separate `initialising` branch, unlike the bell below. The bell
  // needs one because it renders a different badge; here both states say the
  // same sentence, and `initialising` (every required read pending) implies
  // `incomplete` (any read pending) — `useNeedsYou.ts:235,239` — so a branch
  // for it would be dead by subsumption. Mutation-testing found it: deleting it
  // changed no behaviour and killed no test.
  const inboxLabel = !inboxOpen
    ? null
    : needsYou.degraded
      ? needsYou.total > 0
        ? `${needsYou.totalLabel} waiting at the last check`
        : 'The queue could not be read'
      : needsYou.incomplete
        ? 'Still checking what is waiting'
        : needsYou.total > 0
          ? `${needsYou.totalLabel} waiting on you`
          : 'Nothing waiting'

  // An unread queue must never look like an empty one. When a read fails the
  // badge keeps the last known count, and shows "!" rather than disappearing
  // when there is no last known count to keep.
  const bell = (() => {
    const n = needsYou.total
    // Printed count: carries the "+" when the held-drafts page was full. `n` decides.
    const shown = needsYou.totalLabel
    // Nothing has been read yet. An empty array here means "not asked", not
    // "nothing waiting" — a slow or hanging request would otherwise leave the
    // all-clear on screen indefinitely.
    if (needsYou.initialising) {
      return {
        badge: '·',
        title: 'Checking what is waiting on you…',
        label: 'Waiting on you — still checking',
      }
    }
    if (needsYou.degraded) {
      const suffix = needsYou.unknown ? ' — there may be more' : ' at the last check'
      return {
        badge: n > 0 ? (n > 9 ? '9+' : String(n)) : '!',
        title: `Could not check what is waiting${n > 0 ? `; ${shown} open${suffix}` : ''}`,
        label:
          n > 0
            ? `Waiting on you — ${shown} open${suffix}, and the queue could not be refreshed`
            : 'Waiting on you — the queue could not be read',
      }
    }
    // Part of the queue has been read and part has not — entering Shadow, say,
    // where the escalations are known but the held drafts are not yet. What we
    // have is a floor, so it must not be presented as the whole count.
    if (needsYou.incomplete) {
      return {
        badge: n > 0 ? (n > 9 ? '9+' : String(n)) : '·',
        title: n > 0 ? `${shown} waiting on you, and still checking the rest` : 'Still checking what is waiting…',
        label:
          n > 0
            ? `Waiting on you — ${shown} open so far, still checking the rest`
            : 'Waiting on you — still checking',
      }
    }
    return {
      badge: n > 0 ? (n > 9 ? '9+' : String(n)) : null,
      title: n > 0 ? `${shown} waiting on you` : 'Everything waiting on you',
      label: n > 0 ? `Waiting on you — ${shown} open` : 'Waiting on you — nothing open',
    }
  })()
  const isSuperAdmin = !!userMembership?.isSuperAdmin

  // The profile menu's "This month" usage (MAR-317, MAR-413). Read once the menu opens (then
  // cached for five minutes); absent (never an error) when the read is loading, fails, or the
  // plan meters nothing (`useMonthUsage`). Billing data is owner/admin — Account refuses everyone
  // else and subscription-api has no role check — so a member or viewer never reads it here either.
  const canSeeBilling = canAccessFeature(role, 'account')
  const monthUsage = useMonthUsage(currentAccount?.id, profileOpen && canSeeBilling)

  const isActive = (path: string) =>
    location.pathname === path || (path === '/ledger' && location.pathname === '/')

  const navItems: NavItem[] = NAV.map((n) => ({
    key: n.key,
    label: n.label,
    href: n.path,
    icon: <Icon d={n.icon} />,
  }))
  const currentNav = NAV.find((n) => isActive(n.path))?.key
  const go = (href: string) => {
    setProfileOpen(false)
    navigate(href)
  }

  // `?inbox=open` opens the drawer, so the Inbox is deep-linkable even though
  // it is not a route: /notifications and /waiting redirect here, so old links
  // reach the thing they name. The param is stripped once consumed so a later
  // navigation does not reopen it.
  // ⚠️ ONE effect for both params, not one each. Two effects reading the same
  // `location.search` and each calling `navigate(replace: true)` race within a
  // commit — the second computes its new search from the pre-navigation value
  // and can put back the param the first just stripped. No route produces both
  // today, which is the only reason it was invisible.
  useEffect(() => {
    const params = new URLSearchParams(location.search || '')
    const wantsInbox = params.get('inbox') === 'open'
    const wantsAsk = params.get('ask') === 'open'
    if (!wantsInbox && !wantsAsk) return

    if (wantsInbox) {
      setInboxOpen(true)
      params.delete('inbox')
    }
    if (wantsAsk) {
      openAsk()
      params.delete('ask')
    }
    const qs = params.toString()
    navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true })
  }, [location.search, location.pathname, navigate, openAsk])

  // ⌘K opens the Ask dock and focuses its composer.
  //
  // Escape is no longer bound here. The Inbox is a design-system `Drawer`, and
  // every Drawer and Modal registers on ONE Escape stack captured at the
  // document (overlays.md): Escape closes the top dismissible entry and stops.
  // The workspace Menu and the profile Popover dismiss themselves. The dock is
  // the exception it always was: `AskDock` captures Escape at the WINDOW, ahead
  // of that stack, when focus is inside it — Escape in the composer collapses the
  // dock and does nothing else.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        openAsk()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [openAsk])

  const workspaceItems: MenuItem[] = [
    { type: 'label', label: `Workspaces${currentAccount ? ` · ${currentAccount.name}` : ''}` },
    ...workspaces.map((w) => ({
      key: w.id,
      label: w.name,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      description: (w as any).shopify?.shopDomain || undefined,
      checked: w.id === currentWorkspace?.id,
      onSelect: () => switchWorkspace(w.id),
    })),
    // No "Account settings" here — this menu switches workspaces and nothing
    // else. It was a third door to /account, alongside the profile panel's two.
    // Creating one is part of switching, and was lost in the IA rework (MAR-410). Shown to owners
    // and admins of the CURRENT workspace (`canCreateWorkspace`); the server is the authority —
    // `assertAccountWorkspaceCreateAuthority` refuses members — and is a little wider (owner/admin
    // anywhere in the account), so this errs toward hiding, never toward offering a doomed create.
    ...(currentAccount && canCreateWorkspace(role)
      ? [
          { type: 'separator' as const },
          { key: 'new-workspace', label: 'New workspace', icon: <Icon d={PLUS} />, onSelect: () => setNewWorkspaceOpen(true) },
        ]
      : []),
  ]

  // The profile control: an ink Avatar opening a Popover. On the rail at 900px
  // and up; in the topbar below, where it also carries the rail's utilities
  // (theme, Admin, Docs — D-28).
  const profile = (
    <Popover
      label="Account"
      placement={narrow ? 'bottom-end' : 'right-end'}
      open={profileOpen}
      onOpenChange={setProfileOpen}
      trigger={
        <IconButton
          size="lg"
          label={profileLabel}
          aria-current={location.pathname === '/account' ? 'page' : undefined}
          icon={<Avatar name={userInitial} size="sm" tone="ink" label={false} />}
        />
      }
    >
      <CardHeader
        titleAs="p"
        icon={<Avatar name={userInitial} size="md" tone="ink" label={false} />}
        title={`${currentUser?.firstName ?? ''} ${currentUser?.lastName ?? ''}`.trim() || profileName}
        description={currentUser?.email || user?.primaryEmailAddress?.emailAddress}
        aside={shownRole ? <Badge tone="gold">{shownRole}</Badge> : undefined}
      />
      {monthUsage && (
        <>
          <MenuSeparator />
          <MonthUsageSection usage={monthUsage} onOpenBilling={() => go('/account?tab=billing')} />
        </>
      )}
      {currentAccount && (
        <>
          <MenuSeparator />
          <MenuLabel>Account</MenuLabel>
          <MenuItem
            icon={
              <Avatar
                name={initialOf(currentAccount.name, 'A')}
                size="xs"
                shape="square"
                tone="gold"
                label={false}
              />
            }
            label={currentAccount.name}
            checked
          />
        </>
      )}
      {narrow && (
        <>
          <MenuSeparator />
          <ThemeToggle mode={mode} theme={theme} onChange={() => cycleMode()} />
          {isSuperAdmin && <MenuItem icon={<Icon d={ICON_PATHS.admin} />} label="Admin" onClick={() => go('/admin')} />}
          <MenuItem icon={<Icon d={ICON_PATHS.docs} />} label="Docs" onClick={() => go('/docs')} />
        </>
      )}
      <MenuSeparator />
      <MenuItem icon={<Icon d={ICON_PATHS.cog} />} label="Account settings" onClick={() => go('/account')} />
      <MenuItem icon={<Icon d={ICON_PATHS.team} />} label="Manage team" onClick={() => go('/account?tab=team')} />
      {/* Clerk's sign-out alone: the store's session is ended by
          `EndSessionOnSignOut`, the one choke point for every sign-out route. */}
      <MenuItem
        icon={<Icon d={ICON_PATHS.signout} />}
        label="Sign out"
        onClick={() => {
          setProfileOpen(false)
          signOut()
        }}
      />
    </Popover>
  )

  return (
    <ShellContext.Provider value={shellValue}>
      <ToastProvider>
        {/* ⚠️ `--dockw` is declared HERE as well as on <html> (see the layout
          effect above), not on `.mv-app`: custom properties inherit through the
          DOM, and anything rendered outside `.mv-app` must still read it. */}
        {/* ds-dynamic-style: the dock track width, measured by useDockLayout */}
        <div className="mv-root" style={{ '--dockw': `${dock.trackWidth}px` } as CSSProperties}>
          {/* `with-dock` reserves the dock's grid track; below 900px the dock is
            a fixed overlay instead, so the track must NOT be reserved — a
            column would otherwise be held open for an element that has left
            the grid. */}
          <AppGrid ctx={!!ctxPanel} dock={!dock.overlay}>
            {/* ---------------- NAVIGATION ----------------
              The rail at 900px and up; a BottomBar below (D-28). Never both. */}
            {narrow ? (
              <BottomBar
                items={[
                  ...navItems,
                  { key: 'ask', label: 'Ask', href: '?ask=open', icon: <Icon d={ICON_PATHS.search} />, onClick: () => openAsk() },
                ]}
                current={currentNav}
                onNavigate={(item) => go(item.href as string)}
              />
            ) : (
              <NavRail
                items={navItems}
                current={currentNav}
                onNavigate={(item) => go(item.href as string)}
                footer={
                  <>
                    {/* Utilities are buttons; destinations are links (Navigation README). */}
                    <NavItem label="Ask" title="Ask her — ⌘K" icon={<Icon d={ICON_PATHS.search} />} onClick={() => openAsk()} />
                    <ThemeToggle variant="rail" mode={mode} theme={theme} onChange={() => cycleMode()} />
                    {isSuperAdmin && (
                      <NavItem
                        href="/admin"
                        label="Admin"
                        title="Admin"
                        icon={<Icon d={ICON_PATHS.admin} />}
                        current={isActive('/admin')}
                        onClick={() => go('/admin')}
                      />
                    )}
                    <NavItem
                      href="/docs"
                      label="Docs"
                      title="Docs"
                      icon={<Icon d={ICON_PATHS.docs} />}
                      current={isActive('/docs')}
                      onClick={() => go('/docs')}
                    />
                    {profile}
                  </>
                }
              />
            )}

            {/* ---------------- TOPBAR ---------------- */}
            <header className="mv-topbar">
              <div className="mv-topbar-left">
                <div className="mv-topbar-greet">{greet}</div>
                <div className="mv-topbar-sub">{sub}</div>
              </div>
              <div className="mv-topbar-right">
                <IconButton
                  icon={<Icon d={ICON_PATHS.bell} />}
                  label={bell.label}
                  title={bell.title}
                  onClick={openInbox}
                  badge={
                    bell.badge ? (
                      <Badge
                        count={bell.badge}
                        state={needsYou.degraded ? 'stale' : needsYou.incomplete ? 'pending' : undefined}
                      />
                    ) : undefined
                  }
                />
                <span className="mv-topbar-div" aria-hidden="true" />
                {/* v6's `ladderLabel` (prototype L3639): in Shadow the pill says what Shadow
                    means. True since MAR-241 put every shopper-facing send site behind
                    `@marva/shared/send-hold` — until then a reply path could still send. */}
                <TrustPill
                  level={isShadow ? 'shadow' : 'assisted'}
                  label={isShadow ? SHADOW_PILL_LABEL : undefined}
                  compact={narrow}
                  onClick={() => navigate('/authority')}
                />
                <span className="mv-topbar-div" aria-hidden="true" />
                <Menu
                  label="Workspaces"
                  placement="bottom-end"
                  items={workspaceItems}
                  trigger={
                    <Button
                      variant="ghost"
                      size="sm"
                      title={currentWorkspace?.name || 'Workspace'}
                      icon={
                        <Avatar
                          name={initialOf(currentWorkspace?.name, 'W')}
                          size="xs"
                          shape="square"
                          tone="gold"
                          label={false}
                        />
                      }
                      iconRight={<Icon d={CHEV} />}
                    >
                      <span className="mv-topbar-wsname">{currentWorkspace?.name || 'Select workspace'}</span>
                    </Button>
                  }
                />
                {narrow && (
                  <>
                    <span className="mv-topbar-div" aria-hidden="true" />
                    {profile}
                  </>
                )}
              </div>
            </header>

            {/* ---------------- MAIN CANVAS ---------------- */}
            <main className="mv-canvas">
              <NeedsYouContext.Provider value={needsYouValue}>
                <Outlet />
              </NeedsYouContext.Provider>
            </main>

            {/* ---------------- CONTEXT PANEL ---------------- */}
            {ctxPanel && <aside className="mv-ctx">{ctxPanel}</aside>}

            {/* ---------------- ASK DOCK ----------------
              Inside `.mv-app`, and LAST: it is a grid column, so it has to
              be a child of the grid, unlike `InboxDrawer` below, which is a
              fixed-position overlay and sits outside in `.mv-root`.
              After `.mv-ctx` rather than before it because the dock is the
              rightmost column, so DOM order matches reading and tab order
              (`grid-area` makes the placement itself order-independent), and
              because in the overlay band it is `position:fixed` — last in the
              DOM is what puts it over the canvas without a z-index fight. */}
            <AskDock
              collapsed={dock.collapsed}
              overlay={dock.overlay}
              behindDrawer={dock.behindDrawer}
              maxWidth={dock.maxWidth}
              resizable={dock.resizable}
              onCollapsedChange={setDockCollapsed}
              onWidthChange={setDockWidth}
              focusNonce={dockFocus}
              draft={dockDraft}
              surface={surfaceFor(location.pathname)}
              entity={askEntity.askEntity}
              inboxLabel={inboxLabel}
              thread={thread}
            />
          </AppGrid>

          <InboxDrawer
            open={inboxOpen}
            onClose={() => setInboxOpen(false)}
            onExited={inboxShown.onExited}
            workspaceId={currentWorkspace?.id}
            needsYou={needsYou}
          />
          <CreateWorkspaceModal isOpen={newWorkspaceOpen} onClose={() => setNewWorkspaceOpen(false)} />
        </div>
      </ToastProvider>
    </ShellContext.Provider>
  )
}
